import React, { useState, useEffect } from "react";

export default function Header({ t, lang, setLang }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "services", label: t.nav.services },
    { id: "about", label: t.nav.about },
    { id: "gallery", label: t.nav.gallery },
    { id: "testimonials", label: t.nav.testimonials },
    { id: "contact", label: t.nav.contact },
  ];

  const go = (id) => {
    const el = document.getElementById(id);
    if (el) {
      window.scrollTo({ top: el.offsetTop - 70, behavior: "smooth" });
      setOpen(false);
    }
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-slate-200"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
        {/* Logo */}
        <button
          data-testid="brand-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-baseline gap-2"
        >
          <span
            className="text-[20px] md:text-[22px] font-bold tracking-tight text-[#0A0F1C]"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Fasade
          </span>
          <span
            className="text-[20px] md:text-[22px] font-light tracking-[0.18em] uppercase text-[#0F2C29]"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Akershus
          </span>
        </button>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => go(l.id)}
              className="text-sm tracking-wide text-[#0A0F1C] hover:text-[#0F2C29] transition-colors"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              {l.label}
            </button>
          ))}
        </nav>

        {/* Right cluster */}
        <div className="flex items-center gap-3">
          <div
            data-testid="lang-toggle"
            className="hidden sm:flex items-center text-[12px] font-semibold tracking-[0.2em] border border-slate-300 rounded-none"
            role="group"
            aria-label="Language"
          >
            <button
              data-testid="lang-no"
              onClick={() => setLang("no")}
              className={`px-3 py-1.5 transition-colors ${
                lang === "no"
                  ? "bg-[#0F2C29] text-white"
                  : "text-[#0A0F1C] hover:bg-slate-100"
              }`}
            >
              NO
            </button>
            <button
              data-testid="lang-en"
              onClick={() => setLang("en")}
              className={`px-3 py-1.5 transition-colors ${
                lang === "en"
                  ? "bg-[#0F2C29] text-white"
                  : "text-[#0A0F1C] hover:bg-slate-100"
              }`}
            >
              EN
            </button>
          </div>

          <button
            data-testid="header-cta"
            onClick={() => go("contact")}
            className="hidden md:inline-flex bg-[#0F2C29] hover:bg-[#17423E] text-white text-[12px] font-bold tracking-[0.18em] uppercase px-5 py-3 transition-colors"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            {t.nav.cta}
          </button>

          {/* Mobile burger */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden w-10 h-10 flex flex-col items-center justify-center gap-1.5 border border-slate-300"
            aria-label="Menu"
          >
            <span
              className={`block h-px w-5 bg-[#0A0F1C] transition-transform ${
                open ? "translate-y-[3px] rotate-45" : ""
              }`}
            />
            <span
              className={`block h-px w-5 bg-[#0A0F1C] transition-transform ${
                open ? "-translate-y-[3px] -rotate-45" : ""
              }`}
            />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-white border-t border-slate-200"
        >
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`mnav-${l.id}`}
                onClick={() => go(l.id)}
                className="text-left text-base text-[#0A0F1C]"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                {l.label}
              </button>
            ))}
            <div className="flex items-center gap-3 pt-2">
              <div className="flex items-center text-[12px] font-semibold tracking-[0.2em] border border-slate-300">
                <button
                  data-testid="mlang-no"
                  onClick={() => setLang("no")}
                  className={`px-3 py-1.5 ${
                    lang === "no" ? "bg-[#0F2C29] text-white" : "text-[#0A0F1C]"
                  }`}
                >
                  NO
                </button>
                <button
                  data-testid="mlang-en"
                  onClick={() => setLang("en")}
                  className={`px-3 py-1.5 ${
                    lang === "en" ? "bg-[#0F2C29] text-white" : "text-[#0A0F1C]"
                  }`}
                >
                  EN
                </button>
              </div>
              <button
                data-testid="mheader-cta"
                onClick={() => go("contact")}
                className="ml-auto bg-[#0F2C29] text-white text-[12px] font-bold tracking-[0.18em] uppercase px-5 py-3"
              >
                {t.nav.cta}
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
